import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import { CsvExportModule } from "@ag-grid-community/csv-export";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-balham.css";
import { LicenseManager } from "@ag-grid-enterprise/core";
import { ClipboardModule } from "@ag-grid-enterprise/clipboard";
import { ExcelExportModule } from "@ag-grid-enterprise/excel-export";
import { MasterDetailModule } from "@ag-grid-enterprise/master-detail";
import { MenuModule } from "@ag-grid-enterprise/menu";
import { RangeSelectionModule } from "@ag-grid-enterprise/range-selection";
import { SetFilterModule } from "@ag-grid-enterprise/set-filter";
import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client";
import { EventType, InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { css, Global, ThemeProvider as StyledThemeProvider } from "@emotion/react";
import styled from "@emotion/styled";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import BuildIcon from "@mui/icons-material/Build";
import ChecklistIcon from "@mui/icons-material/Checklist";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import {
  AppBar,
  Button,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from "@mui/material";
import { blue, pink } from "@mui/material/colors";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterMoment } from "@mui/x-date-pickers-pro/AdapterMoment";
import "antd/dist/antd.css";
import moment from "moment";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo_white from "./assets/logo_white.svg";
import { loginRequest } from "./authConfig";
import { notifyInteractionRequired, subscribeToInteractionRequired } from "./authEvents";
import { accountSessionKey, isInteractionRequiredError } from "./authUtils";
import { addTelemetry, getTableUpdateTime, getUser } from "./graphql";
import Routes, { isProtectedRoute, preloadRoute } from "./Routes";
import AdvancedSelect from "./shared/AdvancedSelect";
import { AppContext } from "./shared/AppContext";
import { useFeatureAccess } from "./shared/FeatureAccessContext";
import legacyInputStyles from "./shared/legacyInputStyles";
import siteSections from "./shared/siteSections";
import { navigationSections, sectionForPath, TOOLS_VIEWS, visibleToolsViews } from "./shared/toolsNavigation";
// import PullToRefresh from "pulltorefreshjs";
import configEnv from "./environment";

LicenseManager.setLicenseKey(configEnv.AG_GRID_LICENSE_KEY);

const theme = createTheme({
  palette: {
    primary: blue,
    secondary: pink,
    background: {
      default: "#ffffff",
    },
  },
  typography: {
    body2: {
      fontFamily:
        "-apple-system,BlinkMacSystemFont,Segoe UI,PingFang SC,Hiragino Sans GB,Microsoft YaHei,Helvetica Neue,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol;",
    },
  },
});

ModuleRegistry.registerModules([
  ExcelExportModule,
  MasterDetailModule,
  ClipboardModule,
  MenuModule,
  SetFilterModule,
  ClientSideRowModelModule,
  CsvExportModule,
  RangeSelectionModule,
]);

const GlobalStyle = css`
  body {
    font-size: 0.875rem;
    font-weight: 400;
  }
  ${legacyInputStyles}
  .ag-popup {
    display: contents;
  }
  .ag-popup-editor {
    z-index: 1301; /* default 5 but Dialog is set to 1300 */
  }
  .ag-tooltip-custom {
    position: absolute;
    width: auto;
    max-height: 50%;
    overflow: auto;
    z-index: 1301; /* default 1 but Dialog is set to 1300 */
  }
  .custom-tooltip {
    pointer-events: none;
    z-index: 5;
    background-color: #cbd0d3;
    color: #000;
    border-radius: 2px;
    padding: 10px;
    border-width: 1px;
    border-style: solid;
    border-color: #cbd0d3;
    transition: opacity 1s;
    box-shadow: 5px 5px 10px rgba(0, 0, 0, 0.3);
    height: auto !important;
    max-width: 40vw;
  }
  .custom-tooltip p {
    margin-top: 0;
    margin-bottom: 1em;
  }
  .custom-tooltip p:last-child {
    margin-bottom: 0;
  }
  .custom-tooltip td.MuiTableCell-root {
    padding-top: 4px;
    padding-bottom: 4px;
  }
  .custom-tooltip .MuiTableCell-root {
    font-size: 12px;
    font-family:
      -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue",
      sans-serif;
    padding: 0 0 0 10px;
    /* line-height: 26px; */
  }
  .custom-tooltip .MuiTableCell-root:last-of-type {
    padding-right: 10px;
  }
  .custom-tooltip .MuiTableCell-root p {
    margin-bottom: 1em;
  }
  .custom-tooltip .MuiTableCell-root p:last-of-type {
    margin-bottom: 0em;
  }
  .custom-tooltip .center-cell {
    text-align: center;
  }
  .custom-tooltip .highlight-cell {
    font-weight: bold;
    color: #1890ff;
  }
  .custom-tooltip .ql-editor {
    padding: 0;
    overflow-y: inherit;
    line-height: 22px !important;
  }
  .snackbar-warning .MuiSnackbarContent-root {
    background-color: #ff9800;
  }
  .MuiDateTimePickerToolbar-root > span,
  .MuiPickersToolbar-penIconButton {
    display: none !important;
  }
  .MuiFormControl-root {
    width: 100%;
  }
  .MuiPickersToolbar-penIconButton {
    display: none !important;
  }
  .MuiDateRangeCalendar-root > div:first-of-type {
    display: none !important;
  }
`;

const StyledApp = styled.div`
  &.App {
    display: grid;
    box-sizing: border-box;

    grid-template-areas:
      "app-bar"
      "content";

    grid-template-rows: auto 1fr;
    grid-gap: ${(props) => (props.hideNavBar ? "0" : "5px")};
    padding: 20px 20px 40px 20px;

    min-height: 100vh;
    height: ${(props) => (props.workbenchMode ? "100vh" : "auto")};
    overflow: ${(props) => (props.workbenchMode ? "hidden" : "visible")};

    margin-right: auto;
    margin-left: auto;
  }
  @media (min-width: 600px) {
    &.App {
      grid-template-areas: ${(props) => (props.hideNavBar ? '"app-bar" "content"' : '"app-bar" "nav-bar" "content"')};

      grid-template-rows: ${(props) => (props.hideNavBar ? "auto 1fr" : "auto auto 1fr")};
      grid-gap: ${(props) => (props.hideNavBar ? "10px" : "15px")};
    }
  }
  @media (min-width: 768px) {
    &.App {
      width: 768px;
    }
  }
  @media (min-width: 992px) {
    &.App {
      width: 992px;
      padding-bottom: 40px;
    }
  }
  @media (min-width: 1200px) {
    &.App {
      min-width: 1200px;
      width: 90%;
    }
  }
  .app-bar {
    grid-area: app-bar;
    margin: -20px -20px 0 -20px;
    width: auto;
    z-index: 1100 !important;
  }
  .app-bar.compact .MuiToolbar-root {
    min-height: 52px;
    padding-left: 12px;
    padding-right: 12px;
  }
  &.home-app .app-bar-menu {
    display: flex;
    align-items: center;
  }
  @media (min-width: 600px) {
    .app-bar {
      margin: 0;
    }
    .app-bar.compact .MuiToolbar-root {
      min-height: 56px;
    }
  }
  .app-bar-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    background: ${(props) => props.theme.palette.primary.light};
    color: ${(props) => props.theme.palette.primary.contrastText};
  }
  & .app-bar-notice-link {
    cursor: pointer;
    text-decoration: underline;
  }
  .nav-bar {
    box-shadow: inset 0 -1px 0 0 #e0e0e0;
    display: none;
    height: 34px;
    overflow-y: hidden;
  }
  .nav-bar .MuiTabs-root,
  .nav-bar .MuiTab-root {
    min-height: 0;
  }
  .nav-bar .MuiTab-root {
    padding: 6px 16px;
    line-height: 22px;
  }
  .nav-bar .MuiTabs-scrollable {
    overflow-y: hidden;
  }
  @media (min-width: 600px) {
    .nav-bar {
      grid-area: nav-bar;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .nav-bar .MuiTabs-scroller {
      flex-grow: 0;
    }
  }
  .nav-bar-select {
    width: 245px;
  }
  .nav-bar-select .advanced-select__control {
    border: none !important;
    border-radius: unset;
    box-shadow: none;
  }
  .nav-bar-select .advanced-select__value-container {
    font-size: 16px;
    padding: 0 36px;
  }
  .nav-bar-select .advanced-select__single-value {
    color: #f50057;
  }
  .nav-bar-select .advanced-select__indicator-separator {
    display: none;
  }
  .nav-bar-tabs {
    flex: 1;
    justify-content: center;
  }
  .nav-bar-hidden-tab {
    display: none;
  }
  .content {
    grid-area: content;
    min-height: 0;
  }
  .content.tools-route-content {
    /* Scrollable tool tabs must not expand the grid's intrinsic track. */
    min-width: 0;
  }
  .content.home-content {
    display: flex;
    overflow: visible;
    min-width: 0;
  }
  .content.home-content > * {
    flex: 1;
    min-height: 0;
    min-width: 0;
  }
  .content.content-locked {
    display: flex;
    flex-direction: column;
    overflow: visible;
  }
  .content.content-locked > * {
    min-height: 0;
    flex: 1;
  }
  .title {
    flex: 1;
    min-width: 0;
  }
  .title-link {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    padding: 12px;
    cursor: pointer;
  }
  .title-link.compact {
    padding: 8px 6px;
  }
  .title-link.no-click {
    cursor: inherit;
  }
  .title-right {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .app-bar-section-nav {
    width: 210px;
  }
  .app-bar-section-nav .advanced-select__control {
    min-height: 32px;
    border: none !important;
    border-radius: 4px;
    box-shadow: none;
    background-color: rgba(255, 255, 255, 0.12);
  }
  .app-bar-section-nav .advanced-select__single-value,
  .app-bar-section-nav .advanced-select__input-container,
  .app-bar-section-nav .advanced-select__placeholder {
    color: #ffffff;
  }
  .app-bar-section-nav .advanced-select__indicator-separator {
    display: none;
  }
  .app-bar-section-nav .advanced-select__dropdown-indicator {
    color: #ffffff;
  }
  .title-link-logo {
    height: 30px;
    margin-right: 12px;
  }
  .logout-button {
    display: none;
  }
  @media (min-width: 600px) {
    .logout-button {
      display: block;
    }
  }
  @media (max-width: 899px) {
    .app-bar-section-nav {
      display: none;
    }
  }
  .menu-drawer {
    display: inline-flex;
  }
  @media (min-width: 900px) {
    .menu-drawer {
      display: none;
    }
  }
  && .ag-cell {
    padding: 0 2px 0 7px; /* less padding on mobile; date cols worst and need max total 9px here */
    display: flex;
    align-items: center;
  }
  && .ag-cell.ag-right-aligned-cell {
    justify-content: end;
  }
  && .ag-header-cell {
    padding: 0 3px 0 8px; /* less padding on mobile; date cols worst and need max total 11px here */
  }
  && .ag-numeric-cell {
    padding: 0 7px 0 2px; /* less padding on mobile; date cols worst and need max total 9px here */
  }
  && .ag-numeric-header {
    padding: 0 8px 0 3px; /* less padding on mobile; date cols worst and need max total 9px here */
  }
  @media (min-width: 992px) {
    && .ag-cell {
      padding: 0 11px 0 11px;
    }
    && .ag-header-cell {
      padding: 0 12px 0 12px;
    }
    && .ag-numeric-cell {
      padding: 0 11px 0 11px;
    }
    && .ag-numeric-header {
      padding: 0 12px 0 12px;
    }
  }
  && .ag-cell.ag-cell-inline-editing {
    padding: 0;
  }
  && .ag-cell-inline-editing.allowEditingOverflow {
    overflow: visible;
    background-color: inherit !important;
  }
  && .ag-cell-inline-editing .select-cell-editor {
    min-width: 100%;
  }
  .app-attached-antd-dropdown .ant-select-tree-node-content-wrapper:hover {
    background-color: ${(props) => props.theme.palette.primary[100]};
  }
  .ag-full-width-row {
    overflow-y: auto;
  }
  @keyframes rotateY {
    to {
      transform: rotateY(360deg);
    }
  }
  @keyframes spin {
    from {
      transform: scale(1) rotate(0deg);
    }
    to {
      transform: scale(1) rotate(360deg);
    }
  }
`;

const StyledAuthenticationLoader = styled.div`
  &.authentication-loader {
    height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    /* justify-content: center; */
    padding-top: calc(50vh - 150px);
  }
  .logo {
    transform: translate(11%, -12px);
  }
  .progress-container {
    width: 113px;
    display: flex;
    justify-content: space-between;
    margin-left: -2px;
  }
  .progress-container span {
    transition: all 500ms ease;
    background: #777777;
    height: 10px;
    width: 10px;
    display: inline-block;
    border-radius: 10px;
    animation: wave 2s ease infinite;
  }
  .progress-container span:nth-of-type(1) {
    animation-delay: 1000ms;
  }
  .progress-container span:nth-of-type(2) {
    animation-delay: 1100ms;
  }
  .progress-container span:nth-of-type(3) {
    animation-delay: 1200ms;
  }
  .progress-container span:nth-of-type(4) {
    animation-delay: 1300ms;
  }
  .progress-container span:nth-of-type(5) {
    animation-delay: 1400ms;
  }
  .progress-container span:nth-of-type(6) {
    animation-delay: 1500ms;
  }
  .progress-container span:nth-of-type(7) {
    animation-delay: 1600ms;
  }
  .progress-container span:nth-of-type(8) {
    animation-delay: 1700ms;
  }
  @keyframes wave {
    0%,
    40%,
    100% {
      transform: translate(0, 0);
      background-color: #777777;
    }
    10% {
      transform: translate(0, -10px);
      background-color: #4a4a4a;
    }
  }
  .label {
    color: #4a4a4a;
    padding-top: 6px;
    font-size: 1rem;
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-weight: 400;
    line-height: 1.66;
    letter-spacing: 0.03333em;
  }
  .authentication-actions {
    display: flex;
    gap: 8px;
    padding-top: 16px;
  }
`;

// const standalone = window.matchMedia("(display-mode: standalone)").matches;

// if (standalone) {
//   PullToRefresh.init({
//     distThreshold: 100,
//     distMax: 120,
//     distIgnore: 0,
//     triggerElement: ".app-bar",
//     onRefresh() {
//       window.location.reload();
//     },
//   });
// }

const App = (props) => {
  const { instance, inProgress, accounts } = useMsal();
  const apolloClient = useApolloClient();
  const hasCachedAccount = useIsAuthenticated();
  const activeAccount = instance.getActiveAccount();
  const isAuthenticated = hasCachedAccount && Boolean(activeAccount);
  const access = useFeatureAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const profileScope = accountSessionKey(activeAccount);
  // ApolloSessionProvider remounts the application when this account changes.
  const [appUser, setAppUser] = useState(undefined);
  const [authenticationError, setAuthenticationError] = useState("");
  const [appUserLoadAttempt, setAppUserLoadAttempt] = useState(0);
  const interactiveAuthStarted = useRef(false);
  const interactionStatus = useRef(inProgress);
  const homeSessionRestore = useRef({ mounted: false, started: false, superseded: false });
  const [homeSessionChecked, setHomeSessionChecked] = useState(false);
  const [addUserTelemetry] = useMutation(addTelemetry);
  const requiresAuthentication = isProtectedRoute(location.pathname);
  const checkingHomeSession =
    location.pathname === "/" &&
    !activeAccount &&
    !homeSessionChecked &&
    (accounts.length === 0 || homeSessionRestore.current.started);

  interactionStatus.current = inProgress;
  homeSessionRestore.current.onHome = location.pathname === "/";

  useEffect(() => {
    const restore = homeSessionRestore.current;
    restore.mounted = true;
    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.SSO_SILENT_SUCCESS &&
        restore.correlationId &&
        event.payload?.correlationId === restore.correlationId &&
        restore.onHome &&
        !restore.superseded &&
        !instance.getActiveAccount()
      ) {
        const cachedAccounts = instance.getAllAccounts();
        if (
          event.payload.account &&
          cachedAccounts.length === 1 &&
          accountSessionKey(cachedAccounts[0]) === accountSessionKey(event.payload.account)
        ) {
          // Select during the success event, before the MSAL account update
          // renders the account-bound Apollo and feature-access providers.
          instance.setActiveAccount(event.payload.account);
        }
      }
      if ([EventType.LOGIN_START, EventType.LOGOUT_START, EventType.ACTIVE_ACCOUNT_CHANGED].includes(event.eventType)) {
        // Explicit sign-in, sign-out or account selection wins over a late SSO result.
        restore.superseded = true;
        setHomeSessionChecked(true);
      }
    });
    return () => {
      restore.mounted = false;
      instance.removeEventCallback(callbackId);
    };
  }, [instance]);

  useEffect(() => {
    const restore = homeSessionRestore.current;
    if (!checkingHomeSession || inProgress !== InteractionStatus.None || restore.started || restore.superseded) return;
    restore.started = true;
    restore.correlationId = crypto.randomUUID();
    async function restoreSession() {
      try {
        // Redirect processing has finished. Ask Microsoft about an existing
        // session before showing the public homepage as signed out.
        await instance.ssoSilent({ ...loginRequest, correlationId: restore.correlationId });
      } catch {
        // No usable silent session (including blocked iframe SSO): require
        // the existing Login action, never an automatic homepage redirect loop.
      } finally {
        if (restore.mounted) setHomeSessionChecked(true);
      }
    }
    void restoreSession();
  }, [checkingHomeSession, inProgress, instance]);

  useEffect(() => {
    preloadRoute(location.pathname).catch((error) => {
      console.error("Unable to preload route", error);
    });
  }, [location.pathname]);

  const handleLogin = () => {
    if (interactiveAuthStarted.current) {
      return;
    }

    interactiveAuthStarted.current = true;
    setAuthenticationError("");
    setIsLoggingIn(true);
    // Use popup instead of redirect so the main page doesn't flicker through bad states
    instance
      .loginPopup(accounts.length > 1 ? { ...loginRequest, prompt: "select_account" } : loginRequest)
      .catch((error) => {
        if (error?.errorCode !== "user_cancelled") {
          console.error(error);
          setAuthenticationError("Unable to sign in");
        }
      })
      .finally(() => {
        interactiveAuthStarted.current = false;
        setIsLoggingIn(false);
      });
  };

  const handleAuthenticationRetry = () => {
    setAuthenticationError("");
    if (!isAuthenticated && !requiresAuthentication) {
      handleLogin();
      return;
    }

    setAppUserLoadAttempt((attempt) => attempt + 1);
  };

  useEffect(() => {
    // Check the selected account directly, not the effect-lagged boolean.
    if (
      requiresAuthentication &&
      !activeAccount &&
      inProgress === InteractionStatus.None &&
      !interactiveAuthStarted.current &&
      !authenticationError
    ) {
      interactiveAuthStarted.current = true;
      setIsLoggingIn(true);
      instance
        .loginRedirect(accounts.length > 1 ? { ...loginRequest, prompt: "select_account" } : loginRequest)
        .catch((error) => {
          console.error(error);
          interactiveAuthStarted.current = false;
          setIsLoggingIn(false);
          setAuthenticationError("Unable to start sign-in");
        });
    }
  }, [profileScope, accounts.length, authenticationError, inProgress, instance, requiresAuthentication]);

  useEffect(() => {
    return subscribeToInteractionRequired(() => {
      if (interactiveAuthStarted.current || interactionStatus.current !== InteractionStatus.None) {
        return;
      }

      interactiveAuthStarted.current = true;
      setIsLoggingIn(true);
      instance.acquireTokenRedirect({ ...loginRequest, account: instance.getActiveAccount() }).catch((error) => {
        console.error(error);
        interactiveAuthStarted.current = false;
        setIsLoggingIn(false);
        setAuthenticationError("Unable to renew your session");
      });
    });
  }, [instance]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function setUser() {
      setAppUser(undefined);
      if (profileScope === "signed-out") {
        setAppUser(undefined);
        setAuthenticationError("");
        setIsLoggingIn(false);
        return;
      }

      setIsLoggingIn(true);
      setAuthenticationError("");

      try {
        const activeAccount = instance.getActiveAccount();
        if (!activeAccount) {
          throw new Error("No active account is available");
        }

        const azureADUser = {
          email: activeAccount.username,
        };

        const oakUser = await apolloClient.query({
          query: getUser,
          variables: {
            query: {
              in: { col: "email", vals: azureADUser.email },
            },
          },
          fetchPolicy: "no-cache",
          context: { fetchOptions: { signal: controller.signal } },
        });
        const oakProfile = oakUser.data.getUser[0];
        if (!oakProfile) {
          throw new Error(`No application profile exists for ${azureADUser.email}`);
        }
        const user = {
          azureAD: azureADUser,
          oak: { ...oakProfile },
        };

        if (!cancelled) {
          addUserTelemetry({
            variables: {
              user_id: user.oak.id,
              action: "resume session",
              value: location.pathname,
            },
          });
          setAppUser(user);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (isInteractionRequiredError(error)) {
          notifyInteractionRequired(error);
        } else {
          console.error("Unable to load the application user", error);
          setAuthenticationError("Unable to load your profile");
        }
      } finally {
        if (!cancelled) {
          setIsLoggingIn(false);
        }
      }
    }
    setUser();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line
  }, [appUserLoadAttempt, profileScope, apolloClient]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (appUser) {
        if (document.hidden) {
          addUserTelemetry({
            variables: {
              user_id: appUser.oak.id,
              action: "end session",
            },
          });
        } else {
          addUserTelemetry({
            variables: {
              user_id: appUser.oak.id,
              action: "resume session",
              value: location.pathname,
            },
          });
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  });

  //
  // START UPDATE POLLING
  //

  // TODO: What about remote DELETES? -> changed row counts?
  // TODO: Need to somehow apply changes to grids in transactions...

  const [showReloadNotice, setShowReloadNotice] = useState(false);

  const watchTablesConfig = [
    { table: "composite", showReloadNotice: true },
    { table: "credit_composite", showReloadNotice: true },
    { table: "credit_commentary", evictFields: ["getCreditCommentary", "getCreditCommentaryByFriendlyEntity"] },
    { table: "credit_health_rating", evictFields: ["getCreditHealthRating"] },
    { table: "crm_activity", evictFields: ["getCrmActivityLinked", "getCrmActivityLinkedByEntity"] },
    { table: "crm_activity_link", evictFields: ["getCrmActivityLinked", "getCrmActivityLinkedByEntity"] },
    { table: "crm_intermediary", evictFields: ["getCrmIntermediary"] },
    { table: "crm_person", evictFields: ["getCrmPerson"] },
    { table: "crm_status", evictFields: ["getCrmStatus"] },
    { table: "document", evictFields: ["getDocumentLinked", "getDocumentLinkedByEntity", "getDocumentLinkedByToken"] },
    {
      table: "document_link",
      evictFields: ["getDocumentLinked", "getDocumentLinkedByEntity", "getDocumentLinkedByToken"],
    },
    { table: "pipeline", evictFields: ["getPipeline"] },
    { table: "tenant_task", evictFields: ["getTenantTask", "getTenantTaskByToken"] },
    { table: "tenant_task_status", evictFields: ["getTenantTaskStatus", "getTenantTaskStatusByToken"] },
  ];

  const [watchTableCacheTimes, setWatchTableCacheTimes] = useState({});
  const [watchTableUpdateTimes, setWatchTableUpdateTimes] = useState({});

  const [getTableUpdateTimeQuery] = useLazyQuery(getTableUpdateTime, {
    variables: {
      tables: watchTablesConfig.map((x) => x.table),
    },
    pollInterval: 10000,
    fetchPolicy: "no-cache",
    onCompleted: (data) =>
      setWatchTableUpdateTimes(
        JSON.parse(data.getTableUpdateTime).reduce((a, c) => ({ ...a, [c.table]: moment(c.value) }), {}),
      ),
  });
  useEffect(() => {
    async function doStartQuery() {
      if (isAuthenticated) {
        getTableUpdateTimeQuery();
      }
    }
    doStartQuery();
    // eslint-disable-next-line
  }, [isAuthenticated]);

  // const handleActiveQueries = async () => {
  //   const tmp = await apolloClient.getObservableQueries();
  //   console.log(Array.from(tmp).map((x) => x[1].queryName));
  // };

  useEffect(() => {
    async function update() {
      for (const config of watchTablesConfig) {
        const table = config.table;
        if (watchTableUpdateTimes[table] && !watchTableCacheTimes[table]) {
          setWatchTableCacheTimes((prevState) => ({
            ...prevState,
            [table]: watchTableUpdateTimes[table],
          }));
        } else if (watchTableUpdateTimes[table] && watchTableUpdateTimes[table] > watchTableCacheTimes[table]) {
          if (config.showReloadNotice) {
            setShowReloadNotice(true);
          }
          if (config.evictFields) {
            for (const field of config.evictFields) {
              // console.log("EVICTING ", field);
              await apolloClient.refetchQueries({
                updateCache(cache) {
                  cache.evict({ fieldName: field });
                },
              });
            }
          }
          setWatchTableCacheTimes((prevState) => ({
            ...prevState,
            [table]: watchTableUpdateTimes[table],
          }));
        }
      }
    }
    update();
    // eslint-disable-next-line
  }, [watchTableCacheTimes, watchTableUpdateTimes]);

  //
  // END UPDATE POLLING
  //

  // // Scavenge ag-popups
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     var elems = document.querySelectorAll(".ag-popup");
  //     elems.forEach((elem) => {
  //       var toolTipElem = elem.querySelector(".custom-tooltip");
  //       if (toolTipElem) {
  //         var opened = parseInt(toolTipElem.getAttribute("opened"));
  //         if (moment().diff(opened) > 7500) {
  //           elem.parentNode.removeChild(elem);
  //         }
  //       }
  //     });
  //   }, 2500);
  //   return () => clearInterval(interval);
  // }, []);

  const navStruct = useMemo(
    () => navigationSections(siteSections, isAuthenticated ? access : null),
    [access, isAuthenticated],
  );
  const toolsViews = useMemo(() => (isAuthenticated ? visibleToolsViews(access) : []), [access, isAuthenticated]);
  // The visible catalog and current URL are authoritative, including the first
  // render after access is withdrawn. Tools never uses a remembered tab index.
  const section = sectionForPath(navStruct, location.pathname)?.section || false;
  const [navTabValues, setNavTabValues] = useState(
    navStruct.map((x) => x.group).reduce((acc, cur) => ({ ...acc, [cur]: 0 }), {}),
  );

  useEffect(() => {
    async function report() {
      if (appUser) {
        addUserTelemetry({
          variables: {
            user_id: appUser.oak.id,
            action: "navigate",
            value: location.pathname,
          },
        });
      }
    }
    report();
    // eslint-disable-next-line
  }, [location.pathname]);

  useEffect(() => {
    const navItem = sectionForPath(navStruct, location.pathname);
    if (navItem && navItem.group !== "tools") {
      setNavTabValues((prevState) => ({
        ...prevState,
        [navItem.group]: navItem.tabPaths.findIndex(
          (path) => location.pathname === path || location.pathname.startsWith(path + "/"),
        ),
      }));
    }
  }, [location.pathname, navStruct]);

  const handleNavTabValueChange = (group, value) => {
    setNavTabValues((prevState) => ({
      ...prevState,
      [group]: value,
    }));
  };

  const handleSectionChange = (section) => {
    const navItem = navStruct.find((item) => item.section === section?.value);
    if (!navItem) {
      return;
    }

    navigate(navItem.sectionEntryPath || navItem.tabPaths[navTabValues[navItem.group]]);
  };

  const [drawerState, setDrawerState] = useState(false);

  const setDrawer = (state) => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setDrawerState(state);
  };

  const toggleDrawer = () => (event) => {
    if (event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
      return;
    }
    setDrawerState(!drawerState);
  };

  const [drawerMarketOpen, setDrawerMarketOpen] = useState(false);
  const handleDrawerMarketClick = () => {
    setDrawerMarketOpen(!drawerMarketOpen);
  };
  const [drawerActivityOpen, setDrawerActivityOpen] = useState(false);
  const handleDrawerActivityClick = () => {
    setDrawerActivityOpen(!drawerActivityOpen);
  };
  const [drawerCreditOpen, setDrawerCreditOpen] = useState(false);
  const handleDrawerCreditClick = () => {
    setDrawerCreditOpen(!drawerCreditOpen);
  };
  const [drawerPortfolioOpen, setDrawerPortfolioOpen] = useState(false);
  const handleDrawerPortfolioClick = () => {
    setDrawerPortfolioOpen(!drawerPortfolioOpen);
  };
  const [drawerToolsOpen, setDrawerToolsOpen] = useState(false);
  const handleDrawerToolsClick = () => {
    setDrawerToolsOpen(!drawerToolsOpen);
  };

  const navigateHome = () => {
    setDrawerState(false);
    navigate("/");
  };

  const handleReload = () => {
    window.location.reload();
  };

  const handleLogout = () => {
    if (interactiveAuthStarted.current) {
      return;
    }

    interactiveAuthStarted.current = true;
    setIsLoggingIn(true);
    if (appUser?.oak?.id) {
      addUserTelemetry({
        variables: {
          user_id: appUser.oak.id,
          action: "logout",
        },
      });
    }

    setAuthenticationError("");
    setDrawerState(false);

    // Use redirect instead of popup so the main page doesn't flicker through bad states
    instance
      .logoutRedirect({
        postLogoutRedirectUri: "/",
        mainWindowRedirectUri: "/",
      })
      .catch((error) => {
        console.error(error);
        interactiveAuthStarted.current = false;
        setIsLoggingIn(false);
        setAuthenticationError("Unable to sign out");
      });
  };

  const [editingState, setEditingState] = useState({
    edits: [],
    undoQueue: [],
    updated: [],
  });

  const [dataEntryProxy, setDataEntryProxy] = useState();

  const contextValues = {
    appUser,
    setAppUser,
    editingState,
    setEditingState,
    dataEntryProxy,
    setDataEntryProxy,
    showReloadNotice,
  };

  const [noNavUI, setNoNavUI] = useState(false);
  const [noNavTitle, setNoNavTitle] = useState("");
  const isHomeRoute = location.pathname === "/";
  const isToolsRoute = /^\/tools(?:\/|$)/.test(location.pathname);
  const isWorkbenchRoute = /^\/credit-monitor\/workbench(?:\/|$)/.test(location.pathname);
  const useCompactAppBar = true;
  const showAppNavBar = isAuthenticated && !noNavUI;
  const showHeaderSectionChooser = showAppNavBar && !isHomeRoute;
  const showSectionTabs = showAppNavBar && typeof section === "string" && section !== "Tools";
  const siteSectionOptions = navStruct.map((item) => ({ label: item.section, value: item.section }));
  const selectedSiteSection = siteSectionOptions.find((option) => option.value === section) || null;
  // The URL's public section label need not blink during verification. This is
  // placeholder text only: no selected value, Tools option or link is retained.
  const checkingToolsSection =
    access.status === "loading" &&
    TOOLS_VIEWS.some((view) => location.pathname === view.path || location.pathname.startsWith(view.path + "/"));
  useEffect(() => {
    async function doUpdateNoNav() {
      const noNavPaths = [{ re: /^\/tenant-portal/, title: "Blue Owl RE Tenant Portal" }];
      const idx = noNavPaths.findIndex((item) => item.re.test(location.pathname));
      if (idx >= 0) {
        setNoNavUI(true);
        setNoNavTitle(noNavPaths[idx].title);
      } else {
        setNoNavUI(false);
        setNoNavTitle("");
      }
    }
    doUpdateNoNav();
  }, [location.pathname]);

  const showAuthenticationLoader =
    checkingHomeSession ||
    isLoggingIn ||
    inProgress !== InteractionStatus.None ||
    (requiresAuthentication && !isAuthenticated) ||
    (isAuthenticated && !appUser && !authenticationError);

  return showAuthenticationLoader || authenticationError ? (
    <StyledAuthenticationLoader className="authentication-loader">
      <div className="logo">
        <svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" version="1" viewBox="0 0 672 672">
          <path
            d="M 380.500 0.622 C 354.119 3.446, 325.992 14.037, 304.277 29.322 C 292.963 37.286, 275.244 55.029, 267.149 66.500 C 263.656 71.450, 257.806 81.665, 254.149 89.200 L 247.500 102.899 221.750 102.950 L 196 103 196 120.977 L 196 138.954 213.404 139.227 L 230.807 139.500 116.654 390.008 C 53.869 527.787, 1.971 641.697, 1.325 643.140 L 0.150 645.764 14.325 647.995 C 66.988 656.281, 128.733 655.711, 184.500 646.423 C 254.080 634.834, 311.828 610.810, 361 572.995 C 375.294 562.003, 403.872 532.844, 415.059 517.836 C 436.935 488.491, 452.280 459.063, 464.073 423.841 C 475.923 388.448, 483.971 344.398, 485.671 305.613 C 486.302 291.222, 486.410 290.652, 488.912 288.476 C 490.335 287.238, 494.775 283.424, 498.777 280.001 C 517.847 263.688, 535.114 237.363, 543.852 211.282 C 549.741 193.706, 551.485 181.830, 551.493 159.250 L 551.500 139 568.750 139 L 586 139 586 121 L 586 103 563.500 103 C 544.344 103, 541 102.781, 541 101.526 C 541 98.598, 527.823 74.287, 521.237 65.066 C 498.817 33.671, 462.548 10.449, 424.265 2.977 C 413.784 0.931, 389.713 -0.364, 380.500 0.622 M 383 36.456 C 382.175 36.656, 378.125 37.366, 374 38.034 C 358.795 40.495, 342.005 47.286, 327.500 56.843 C 318.804 62.573, 304.503 76.220, 298.068 84.930 C 293.586 90.997, 287 101.478, 287 102.544 C 287 102.795, 292.512 103.002, 299.250 103.004 C 314.368 103.009, 327.390 105.047, 340.651 109.483 C 357.785 115.214, 371.330 122.656, 385.609 134.184 L 393.612 140.645 399.025 135.797 C 411.081 124.999, 430.679 113.961, 446.500 109.058 C 459.752 104.951, 467.470 103.770, 485.202 103.134 L 502.903 102.500 498.467 95.161 C 484.353 71.811, 462.890 53.697, 437.833 43.988 C 425.850 39.345, 413.029 36.901, 398.500 36.489 C 390.800 36.271, 383.825 36.256, 383 36.456 M 267.664 144.159 C 266.349 147.097, 241.402 201.925, 212.226 266 C 80.007 556.371, 56.002 609.335, 56.424 609.757 C 57.286 610.619, 78.593 607.834, 93.046 604.971 C 173.028 589.124, 240.295 540.417, 288.224 463.644 C 303.497 439.180, 321.524 401.230, 331.520 372.500 C 333.625 366.450, 335.405 361.402, 335.475 361.282 C 335.546 361.162, 338.505 361.948, 342.052 363.030 C 345.598 364.112, 353.200 365.910, 358.944 367.026 C 364.689 368.142, 369.562 369.229, 369.774 369.441 C 369.986 369.652, 368.499 374.477, 366.470 380.163 C 329.443 483.906, 272.804 558.441, 196.593 603.716 C 191.592 606.687, 188.625 608.870, 190 608.566 C 191.375 608.262, 196.775 607.094, 202 605.972 C 276.142 590.039, 337.387 555.563, 378.705 506.500 C 409.712 469.681, 431.659 421.928, 442.520 367.650 C 445.507 352.719, 448.593 331.331, 449.599 318.585 C 450.270 310.079, 450.215 309.706, 448.401 310.460 C 443.614 312.449, 433.979 315.180, 424.668 317.188 C 410.776 320.183, 382.331 320.403, 367.500 317.630 C 355.103 315.312, 339.639 310.333, 328.219 304.981 C 309.627 296.269, 286.441 278.178, 274.911 263.387 L 270.322 257.500 278.746 239 C 283.378 228.825, 287.418 220.275, 287.721 220 C 288.025 219.725, 288.492 220.115, 288.759 220.868 C 290.190 224.895, 301.920 240.732, 308.235 247.160 C 357.099 296.901, 434.424 296.346, 482.518 245.909 C 499.213 228.400, 510.798 205.330, 515.112 181 C 516.576 172.743, 516.926 148.714, 515.671 142.657 L 514.875 138.814 491.687 139.253 C 465.240 139.753, 460.612 140.660, 444.340 148.528 C 428.148 156.357, 416.713 166.194, 401.525 185.359 C 397.738 190.137, 394.250 193.917, 393.772 193.757 C 393.295 193.598, 388.089 187.874, 382.202 181.036 C 376.316 174.198, 367.817 165.632, 363.315 162 C 353.764 154.294, 339.039 146.350, 327 142.409 C 318.848 139.741, 317.508 139.610, 294.277 139.223 L 270.055 138.819 267.664 144.159"
            stroke="none"
            fill="#042c5c"
            fillRule="evenodd"
          />
        </svg>
      </div>
      {!authenticationError && (
        <div className="progress-container">
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
          <span></span>
        </div>
      )}
      <div className="label">{authenticationError || (checkingHomeSession ? "Checking sign-in..." : "Loading")}</div>
      {authenticationError && (
        <div className="authentication-actions">
          <Button variant="contained" color="primary" onClick={handleAuthenticationRetry}>
            Retry
          </Button>
          {isAuthenticated && (
            <Button variant="outlined" color="primary" onClick={handleLogout}>
              Sign Out
            </Button>
          )}
        </div>
      )}
    </StyledAuthenticationLoader>
  ) : (
    <StyledApp
      className={`App${isHomeRoute ? " home-app" : ""}`}
      id="app"
      hideNavBar={!showAppNavBar || isHomeRoute}
      workbenchMode={isWorkbenchRoute}
    >
      <AppBar className={`app-bar ${useCompactAppBar ? "compact" : ""}`} position="static">
        <Toolbar>
          <div className="title">
            {!noNavUI && (
              <div className={`title-link ${useCompactAppBar ? "compact" : ""}`} onClick={navigateHome}>
                <img className="title-link-logo" src={logo_white} alt="" />
                <Typography className="title-link-text" variant="h6" color="inherit">
                  Blue Owl Real Estate QR
                </Typography>
              </div>
            )}
            {noNavUI && (
              <div className={`title-link no-click ${useCompactAppBar ? "compact" : ""}`}>
                <img className="title-link-logo" src={logo_white} alt="" />
                <Typography className="title-link-text" variant="h6" color="inherit">
                  {noNavTitle}
                </Typography>
              </div>
            )}
          </div>
          {!noNavUI && (
            <div className="app-bar-menu">
              {isAuthenticated ? (
                <Fragment>
                  <div className="title-right">
                    {showHeaderSectionChooser && (
                      <AdvancedSelect
                        extraClassName="app-bar-section-nav"
                        options={siteSectionOptions}
                        isLoading={access.status === "loading"}
                        aria-label={
                          access.status === "loading" ? "Select section, checking Tools access" : "Select section"
                        }
                        placeholder={checkingToolsSection ? "Tools" : "Select section"}
                        value={selectedSiteSection}
                        onChange={handleSectionChange}
                      />
                    )}
                    {/* <Button className="logout-button" onClick={handleActiveQueries} color="inherit">
                      GET ACTIVE QUERIES
                    </Button>
                    <Button className="logout-button" onClick={handleEvict} color="inherit">
                      EVICT
                    </Button> */}
                    <Button className="logout-button" onClick={handleLogout} color="inherit">
                      Logout
                    </Button>
                  </div>
                  <IconButton className="menu-drawer" color="inherit" onClick={toggleDrawer(true)}>
                    <MenuIcon />
                  </IconButton>
                </Fragment>
              ) : (
                <Fragment>
                  <Button onClick={handleLogin} color="inherit">
                    Login
                  </Button>
                </Fragment>
              )}
            </div>
          )}
        </Toolbar>
        {showReloadNotice && !noNavUI && (
          <div className="app-bar-notice">
            <span>New Data Available, Please&nbsp;</span>
            <span className="app-bar-notice-link" onClick={handleReload}>
              Reload
            </span>
            <span>&nbsp;Page</span>
          </div>
        )}
      </AppBar>

      {showSectionTabs ? (
        <div className="nav-bar">
          {
            {
              "Market Monitor": (
                <Tabs
                  className="nav-bar-tabs"
                  value={navTabValues.marketMonitor}
                  onChange={(event, value) => handleNavTabValueChange("marketMonitor", value)}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  indicatorColor="secondary"
                  textColor="primary"
                >
                  <Tab label="Sourcing Model" component={Link} to="/market-monitor/sourcing-model" />
                  <Tab label="Universe Explorer" component={Link} to="/market-monitor/universe-explorer" />
                  <Tab label="Research View" component={Link} to="/market-monitor/research" />
                </Tabs>
              ),
              "Activity Monitor": (
                <Tabs
                  className="nav-bar-tabs"
                  value={navTabValues.activityMonitor}
                  onChange={(event, value) => handleNavTabValueChange("activityMonitor", value)}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  indicatorColor="secondary"
                  textColor="primary"
                >
                  {/* <Tab label="Dashboard" component={Link} to="/activity-monitor/dashboard" /> */}
                  <Tab label="Pipeline" component={Link} to="/activity-monitor/pipeline" />
                  <Tab label="CRM" component={Link} to="/activity-monitor/crm" />
                  <Tab label="Tasks" component={Link} to="/activity-monitor/tasks" />
                  <Tab label="Tenant Tasks" component={Link} to="/activity-monitor/tenant_tasks" />
                </Tabs>
              ),
              "Credit Monitor": isWorkbenchRoute ? (
                <Tabs
                  className="nav-bar-tabs"
                  value={0}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  indicatorColor="secondary"
                  textColor="primary"
                >
                  <Tab label="Overview" value={0} component={Link} to="/credit-monitor/workbench" />
                </Tabs>
              ) : (
                <Tabs
                  className="nav-bar-tabs"
                  value={navTabValues.creditMonitor}
                  onChange={(event, value) => handleNavTabValueChange("creditMonitor", value)}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  indicatorColor="secondary"
                  textColor="primary"
                >
                  <Tab label="Dashboard" value={1} component={Link} to="/credit-monitor/dashboard" />
                  <Tab label="Detail View" value={2} component={Link} to="/credit-monitor/detail" />
                  <Tab label="Research View" value={3} component={Link} to="/credit-monitor/research" />
                </Tabs>
              ),
              "Portfolio Data": (
                <Tabs
                  className="nav-bar-tabs"
                  value={navTabValues.portfolioData}
                  onChange={(event, value) => handleNavTabValueChange("portfolioData", value)}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  indicatorColor="secondary"
                  textColor="primary"
                >
                  <Tab label="Dashboard" component={Link} to="/portfolio/dashboard" />
                  <Tab label="Property View" component={Link} to="/portfolio/property" />
                  <Tab label="Purchase View" component={Link} to="/portfolio/purchase" />
                  <Tab label="Sale View" component={Link} to="/portfolio/sale" />
                  <Tab label="Funding View" component={Link} to="/portfolio/funding" />
                  <Tab label="Lease View" component={Link} to="/portfolio/lease" />
                  <Tab label="Due Diligence View" component={Link} to="/portfolio/due-diligence" />
                  <Tab label="Disposition Model" component={Link} to="/portfolio/disposition-model" />
                </Tabs>
              ),
            }[section]
          }
        </div>
      ) : null}
      {!noNavUI && (
        <Drawer className="nav-drawer" anchor="top" open={drawerState} onClose={setDrawer(false)}>
          <div>
            {showReloadNotice && <div className="notice-spacer"></div>}
            <div className="toolbar-spacer"></div>
            <List>
              <ListItem button onClick={handleDrawerMarketClick}>
                <ListItemIcon>
                  <TrackChangesIcon />
                </ListItemIcon>
                <ListItemText primary="Market Monitor" />
                {drawerMarketOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
              <Collapse in={drawerMarketOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="sourcing-model"
                    component={Link}
                    to="/market-monitor/sourcing-model"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Sourcing Model" />
                  </ListItem>
                  <ListItem
                    button
                    className="nested"
                    key="universe-explorer"
                    component={Link}
                    to="/market-monitor/universe-explorer"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Universe Explorer" />
                  </ListItem>
                  <ListItem
                    button
                    className="nested"
                    key="research"
                    component={Link}
                    to="/market-monitor/research"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Research View" />
                  </ListItem>
                </List>
              </Collapse>
              <ListItem button onClick={handleDrawerActivityClick}>
                <ListItemIcon>
                  <ChecklistIcon />
                </ListItemIcon>
                <ListItemText primary="Activity Monitor" />
                {drawerActivityOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
              <Collapse in={drawerActivityOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {/* <ListItem
                  button
                  className="nested"
                  key="dashboard"
                  component={Link}
                  to="/activity-monitor/dashboard"
                  onClick={setDrawer(false)}
                  onKeyDown={setDrawer(false)}
                >
                  <ListItemIcon>
                    <ChevronRightIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard" />
                </ListItem> */}
                  <ListItem
                    button
                    className="nested"
                    key="pipeline"
                    component={Link}
                    to="/activity-monitor/pipeline"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Pipeline" />
                  </ListItem>
                  <ListItem
                    button
                    className="nested"
                    key="crm"
                    component={Link}
                    to="/activity-monitor/crm"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="CRM" />
                  </ListItem>
                  <ListItem
                    button
                    className="nested"
                    key="tasks"
                    component={Link}
                    to="/activity-monitor/tasks"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Tasks" />
                  </ListItem>
                  <ListItem
                    button
                    className="nested"
                    key="tenant_tasks"
                    component={Link}
                    to="/activity-monitor/tenant_tasks"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Tenant Tasks" />
                  </ListItem>
                </List>
              </Collapse>
              <ListItem button onClick={handleDrawerCreditClick}>
                <ListItemIcon>
                  <BlurOnIcon />
                </ListItemIcon>
                <ListItemText primary="Credit Monitor" />
                {drawerCreditOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
              <Collapse in={drawerCreditOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="credit-monitor/dashboard"
                    component={Link}
                    to="/credit-monitor/dashboard"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                  </ListItem>
                  <ListItem
                    button
                    className="nested"
                    key="credit-monitor/detail"
                    component={Link}
                    to="/credit-monitor/detail"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Detail View" />
                  </ListItem>
                  <ListItem
                    button
                    className="nested"
                    key="credit-monitor/research"
                    component={Link}
                    to="/credit-monitor/research"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Research View" />
                  </ListItem>
                </List>
              </Collapse>
              <ListItem button onClick={handleDrawerPortfolioClick}>
                <ListItemIcon>
                  <TimelineIcon />
                </ListItemIcon>
                <ListItemText primary="Portfolio Data" />
                {drawerPortfolioOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItem>
              <Collapse in={drawerPortfolioOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="dashboard"
                    component={Link}
                    to="/portfolio/dashboard"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Dashboard" />
                  </ListItem>
                </List>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="property-view"
                    component={Link}
                    to="/portfolio/property"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Property View" />
                  </ListItem>
                </List>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="purchase-view"
                    component={Link}
                    to="/portfolio/purchase"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Purchase View" />
                  </ListItem>
                </List>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="sale-view"
                    component={Link}
                    to="/portfolio/sale"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Sale View" />
                  </ListItem>
                </List>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="funding-view"
                    component={Link}
                    to="/portfolio/funding"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Funding View" />
                  </ListItem>
                </List>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="lease-view"
                    component={Link}
                    to="/portfolio/lease"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Lease View" />
                  </ListItem>
                </List>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="due-diligence-view"
                    component={Link}
                    to="/portfolio/due-diligence"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Due Diligence View" />
                  </ListItem>
                </List>
                <List component="div" disablePadding>
                  <ListItem
                    button
                    className="nested"
                    key="disposition-model"
                    component={Link}
                    to="/portfolio/disposition-model"
                    onClick={setDrawer(false)}
                    onKeyDown={setDrawer(false)}
                  >
                    <ListItemIcon>
                      <ChevronRightIcon />
                    </ListItemIcon>
                    <ListItemText primary="Disposition Model" />
                  </ListItem>
                </List>
              </Collapse>
              {toolsViews.length > 0 && (
                <Fragment>
                  <ListItem button onClick={handleDrawerToolsClick} aria-expanded={drawerToolsOpen}>
                    <ListItemIcon>
                      <BuildIcon />
                    </ListItemIcon>
                    <ListItemText primary="Tools" />
                    {drawerToolsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItem>
                  <Collapse in={drawerToolsOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {toolsViews.map((view) => (
                        <ListItem
                          button
                          className="nested"
                          key={view.id}
                          component={Link}
                          to={view.path}
                          onClick={setDrawer(false)}
                          onKeyDown={setDrawer(false)}
                        >
                          <ListItemIcon>
                            <ChevronRightIcon />
                          </ListItemIcon>
                          <ListItemText primary={view.label} />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Fragment>
              )}
            </List>
            {isAuthenticated && (
              <Fragment>
                <Divider />
                <List>
                  <ListItem button key="logout" onClick={handleLogout}>
                    <ListItemIcon>
                      <AccountCircleIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                  </ListItem>
                </List>
              </Fragment>
            )}
          </div>
        </Drawer>
      )}
      <div
        className={`content ${isWorkbenchRoute ? "content-locked" : ""} ${isHomeRoute ? "home-content" : ""} ${isToolsRoute ? "tools-route-content" : ""}`}
      >
        <AppContext.Provider value={contextValues}>
          <Routes />
        </AppContext.Provider>
      </div>
    </StyledApp>
  );
};

const WithProvider = () => (
  <LocalizationProvider dateAdapter={AdapterMoment}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Global styles={GlobalStyle} />
      <StyledThemeProvider theme={theme}>
        <App />
      </StyledThemeProvider>
    </ThemeProvider>
  </LocalizationProvider>
);

export default WithProvider;
